import IconAppsIntegrations from "@/icons/IconAppsIntegrations";
import IconAutomation from "@/icons/IconAutomation";
import IconActivity from "@/icons/IconActivity";
import IconBilling from "@/icons/IconBilling";
import IconChannels from "@/icons/IconChannels";
import IconGeneralSettings from "@/icons/IconGeneralSettings";
import IconHome from "@/icons/IconHome";
import IconWorkflows from "@/icons/IconWorkflows";
import { MenuItem } from "@/types";
import React, { useMemo } from "react";
import useSubscriptions from "./useSubscriptions";
import IconKnowledgeBase from "@/icons/IconKnowledgeBase";
import IconDesigner from "@/icons/IconDesigner";
import IconDeployment from "@/icons/IconDeployment";
import IconHelp from "@/icons/IconHelp";
import IconEmail from "@/icons/IconEmail";
import IconCalendarCheck from "@/icons/IconCalendarCheck";
import IconInsightsReports from "@/icons/IconInsightsReports";
import IconPersonTeam from "@/icons/IconPersonTeam";
import AuthService from "@/services/AuthService";

export default function useMenuItems() {
  const { subscription } = useSubscriptions();
  const isVelaroInternal = AuthService.profile.siteId === 1032;
  const isVelaroAdmin = AuthService.profile.isVelaroAdmin === true;
  const isVelaroBilling = AuthService.profile.isVelaroBilling === true;
  const isVelaroEmail = AuthService.profile.userName?.endsWith("@velaro.com") === true;
  // Staff = explicit IsVelaroSupport or IsVelaroAdmin DB flag — never inferred from siteId or email alone
  const isVelaroStaff = AuthService.profile.isVelaroSupport === true || isVelaroAdmin;
  // Billing access = full admin OR dedicated billing role (IsVelaroBilling)
  const hasBillingAccess = (isVelaroAdmin || isVelaroBilling) && isVelaroEmail;

  const menuItems: MenuItem[] = useMemo(() => {
    return [
      {
        label: "Dashboard",
        to: "/dashboard",
        icon: <IconHome />,
        enabled: true
      },
      {
        label: "Settings",
        icon: <IconGeneralSettings />,
        sublinks: [
          {
            label: "Account",
            to: "/account",
            end: true,
            enabled: true
          },
          {
            label: "Users",
            to: "/users",
            enabled: true
          },
          {
            label: "Teams",
            to: "/teams",
            enabled: true
          },
          {
            label: "Routing & Escalation",
            to: "/routing/settings",
            enabled: true
          },
          {
            label: "Schedules",
            to: "/work_schedules",
            enabled: true
          },
          {
            label: "Skills",
            to: "/skills",
            enabled: true
          },
          {
            label: "Surveys",
            to: "/surveys",
            enabled: true
          },
          // Deployment promoted to its own top-level nav section below
          {
            label: "Invitations",
            to: "/invitations",
            enabled: subscription?.enableInvitations
          },
          {
            label: "Tags",
            to: "/tags",
            enabled: true
          },
          {
            label: "Custom Fields",
            to: "/custom-fields",
            enabled: true
          },
          {
            label: "Canned Responses",
            to: "/canned_responses",
            enabled: true
          },
          {
            label: "File Storage",
            to: "/files",
            enabled: true
          },
          {
            label: "Transcript Settings",
            to: "/account/transcript-settings",
            enabled: true
          },
          {
            label: "Usage",
            to: "/account/usage",
            enabled: true
          },
          {
            label: "Settings Hub",
            to: "/settings/hub",
            enabled: true
          },
          {
            label: "Advanced Settings",
            to: "/settings",
            end: true,
            enabled: true
          },
          {
            label: "Roles & Permissions",
            to: "/settings/roles",
            enabled: true
          },
          {
            label: "AI Provider",
            to: "/settings/ai-provider",
            enabled: true
          },
          {
            label: "Conversions",
            to: "/settings/conversions",
            enabled: true
          },
          {
            label: "Condition Presets",
            to: "/settings/condition-presets",
            enabled: true
          },
          {
            label: "Compliance",
            to: "/settings/compliance",
            enabled: subscription?.enableComplianceMode
          }
        ],
        enabled: true
      },
      {
        label: "Designer",
        icon: <IconDesigner />,
        enabled: true,
        sublinks: [
          {
            label: "Chat Window",
            to: "/chat_window",
            enabled: true,
            end: false
          }
        ]
      },
      {
        label: "Deployment",
        icon: <IconDeployment />,
        enabled: true,
        sublinks: [
          {
            label: "Chat Embedding",
            to: "/deployment",
            enabled: true
          },
          {
            label: "Preview Chat",
            to: "/chat-preview",
            enabled: true
          },
          {
            label: "Page Widgets",
            to: "/page-widgets",
            enabled: true
          },
          {
            label: "Proactive Invitations",
            to: "/proactive-engagement",
            enabled: true
          }
        ]
      },
      {
        label: "Workflows",
        icon: <IconWorkflows />,
        enabled: true,
        sublinks: [
          {
            label: "My Workflows",
            to: "/workflows",
            enabled: true
          },
          {
            label: "Variable Guide",
            to: "/workflows/variable-guide",
            enabled: true
          }
        ]
      },
      {
        label: "Visitor Monitor",
        to: "/visitor-monitor",
        icon: <IconPersonTeam />,
        enabled: subscription?.enableVisitorTracking
      },
      {
        label: "Outreach",
        icon: <IconEmail />,
        enabled: !!(subscription?.enableEmailCampaigns || subscription?.enableIvr),
        sublinks: [
          {
            label: "Email Campaigns",
            to: "/email-campaigns",
            enabled: subscription?.enableEmailCampaigns
          },
          {
            label: "IVR Campaigns",
            to: "/ivr-campaigns",
            enabled: subscription?.enableIvr
          },
          {
            label: "Segments",
            to: "/contact-segments",
            enabled: subscription?.enableEmailCampaigns
          }
        ]
      },
      {
        label: "Appointments",
        to: "/appointments",
        icon: <IconCalendarCheck />,
        enabled: subscription?.enableAppointments
      },
      // {
      //   label: "Bots and Automation",
      //   to: "/bots",
      //   icon: <IconAutomation />,
      //   enabled: subscription?.enableAutomation
      // },
      // JEROME: The old "AI Chatbot" menu has been hidden (enabled: false) on 2026-03-08.
      // The code is preserved below — do NOT remove it. To restore, change enabled back to true
      // (or subscription?.enableAI). The new "AI Chatbot" menu (formerly "AI Chatbot New") is
      // now the only visible AI chatbot entry and includes Chatbot Profile.
      {
        label: "AI Chatbot (Legacy — Hidden)",
        icon: <IconAutomation />,
        enabled: false, // JEROME: intentionally disabled — was: true / subscription?.enableAI
        sublinks: [
          {
            label: "Chatbot Settings",
            to: "/ai",
            enabled: true
          },
          {
            label: "Chatbot Profile",
            to: "/bots",
            enabled: true
          },
          {
            label: "Chatbot Training",
            to: "/bots/training/index",
            enabled: true
          }
        ]
      },
      {
        label: "AI Chatbot",
        icon: <IconAutomation />,
        enabled: true,
        sublinks: [
          {
            label: "Bot Setup Wizard",
            to: "/chatbots/new",
            enabled: true
          },
          {
            label: "Bot Personas",
            to: "/bots",
            enabled: true
          },
          {
            label: "AI Configurations",
            to: "/ai-new/settings",
            enabled: true
          },
          {
            label: "Chatbot Training",
            to: "/ai-new/training",
            enabled: true
          },
          {
            label: "AI Indexes",
            to: "/ai-new/indexes",
            enabled: true
          }
        ]
      },
      // Rules panel visibility is a pure UI toggle. Saved Rules continue to fire at
      // runtime regardless of this flag. Default: existing customers see the panel
      // (Startup guard sets ShowRulesPanel=1 for existing rows), new signups do not.
      // The /rules route remains registered in Routes.tsx for direct URL access.
      ...(subscription?.showRulesPanel
        ? [
            {
              label: "Rules",
              icon: <IconWorkflows />,
              enabled: true,
              sublinks: [
                {
                  label: "Rules",
                  to: "/rules",
                  enabled: true
                },
                {
                  label: "Routing History",
                  to: "/routing-history",
                  enabled: true
                },
                {
                  label: "Routing Activity",
                  to: "/routing/activity",
                  enabled: true
                }
              ]
            }
          ]
        : []),
      {
        label: "Focus Inbox",
        to: "/focus-inbox",
        icon: (
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
          </svg>
        ),
        // Show if enabled; always show as a menu item so upsell is visible to all users
        enabled: true
      },
      {
        label: "What's Running",
        to: "/whats-running",
        icon: <IconActivity />,
        enabled: true
      },
      {
        label: "Billing",
        icon: <IconBilling />,
        enabled: true,
        sublinks: [
          // Customer-facing — always visible to all users.
          {
            label: "Usage & Plan",
            to: "/billing",
            end: true,
            enabled: true
          },
          // Operations — Velaro billing staff only (IsVelaroAdmin OR IsVelaroBilling + @velaro.com).
          {
            label: "Billing Guide",
            to: "/admin/billing-guide",
            enabled: hasBillingAccess
          },
          {
            label: "Financial Dashboard",
            to: "/admin/billing-financial",
            enabled: hasBillingAccess
          },
          {
            label: "Per-Site Billing",
            to: "/admin/billing-staff",
            enabled: hasBillingAccess
          },
          {
            label: "Plans & Pricing",
            to: "/admin/plans-pricing",
            enabled: hasBillingAccess
          },
          {
            label: "Overage & Auto-Upgrade",
            to: "/admin/billing-overage",
            enabled: hasBillingAccess
          },
          {
            label: "Subscription Settings",
            to: "/admin/subscription-settings",
            enabled: hasBillingAccess
          },
          {
            label: "New Customer Setup",
            to: "/admin/subscription-new",
            enabled: hasBillingAccess
          },
          {
            label: "Quote Builder",
            to: "/admin/pricing-quote",
            enabled: hasBillingAccess
          },
          // Documents & Config
          {
            label: "Invoices",
            to: "/billing/invoices",
            enabled: hasBillingAccess
          },
          {
            label: "Contracts",
            to: "/billing/contracts",
            enabled: hasBillingAccess
          },
          {
            label: "Templates",
            to: "/billing/templates",
            enabled: hasBillingAccess
          },
          {
            label: "Usage Pack Purchases",
            to: "/billing/usage-packs",
            enabled: hasBillingAccess
          },
          {
            label: "Email Campaign Usage",
            to: "/billing/campaign-usage",
            enabled: hasBillingAccess
          },
          {
            label: "Stripe Settings",
            to: "/billing/settings",
            enabled: hasBillingAccess
          },
          {
            label: "QuickBooks Mapping",
            to: "/billing/qb-mapping",
            enabled: hasBillingAccess
          },
          {
            label: "StaxBill Import",
            to: "/admin/billing-financial?tab=staxbill",
            enabled: hasBillingAccess
          },
        ]
      },
      {
        label: "Channels",
        icon: <IconChannels />,
        sublinks: [
          {
            label: "Facebook",
            to: "/facebook",
            enabled: subscription?.enableFacebook
          },
          {
            label: "Instagram",
            to: "/instagram",
            enabled: subscription?.enableInstagram
          },
          {
            label: "TikTok",
            to: "/tiktok",
            enabled: subscription?.enableTikTok
          },
          {
            label: "SMS",
            to: "/sms",
            enabled: subscription?.enableSms
          },
          {
            label: "WhatsApp",
            to: "/whatsapp",
            enabled: subscription?.enableWhatsapp
          },
          {
            label: "RCS",
            to: "/rcs",
            enabled: subscription?.enableRcs
          },
          {
            label: "IVR",
            to: "/ivr",
            enabled: subscription?.enableIvr
          },
          {
            label: "Click-to-Call",
            to: "/click-to-call",
            enabled: subscription?.enableClickToCallWidget
          },
          {
            label: "AI Video Avatars",
            to: "/avatar",
            enabled: subscription?.enableAvatarMode
          }
        ],
        enabled:
          subscription?.enableFacebook ||
          subscription?.enableInstagram ||
          subscription?.enableTikTok ||
          subscription?.enableSms ||
          subscription?.enableWhatsapp ||
          subscription?.enableRcs ||
          subscription?.enableIvr ||
          subscription?.enableClickToCallWidget ||
          subscription?.enableAvatarMode
      },
      {
        label: "Knowledge Base",
        to: "/kb",
        icon: <IconKnowledgeBase />,
        sublinks: [
          {
            label: "Settings",
            to: "/kb/settings",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Topics",
            to: "/kb/topics",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Articles",
            to: "/kb/articles",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Site Map",
            to: "/kb/map",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Performance",
            to: "/kb/performance",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Search Widget",
            to: "/kb/widget",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Asset Library",
            to: "/kb/assets",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Import",
            to: "/kb/migrate",
            enabled: subscription?.enableKbImport ?? subscription?.enableKnowledgeBase
          }
        ],
        enabled: subscription?.enableKnowledgeBase
      },
      {
        label: "Reports",
        icon: <IconInsightsReports />,
        enabled: true,
        sublinks: [
          { label: "Value Report", to: "/reports/value", enabled: true },
          { label: "Conversions", to: "/reports/conversions", enabled: true },
          { label: "Agent Performance", to: "/reports/agent-performance", enabled: true },
          { label: "Leaderboard", to: "/reports/leaderboard", enabled: subscription?.enableAgentLeaderboard ?? false },
          { label: "Chat Intelligence", to: "/reports/chat-intelligence", enabled: true },
          { label: "CSAT Results", to: "/reports/csat", enabled: true },
          { label: "IVR Outbound", to: "/reports/ivr-campaigns", enabled: subscription?.enableOutboundIvr ?? false },
          { label: "Call Center Analytics", to: "/reports/call-center", enabled: true },
          { label: "Agent Connectivity", to: "/reports/agent-connectivity", enabled: true },
          { label: "Call Attribution", to: "/reports/call-attribution", enabled: true }
        ]
      },
      {
        label: "Integrations",
        to: "/integrations",
        icon: <IconAppsIntegrations />,
        sublinks: [
          {
            label: "All Integrations",
            to: "/integrations",
            enabled: true,
            end: true
          },
          // ── CRM ──
          { label: "Velaro CRM", to: "/integrations/velaro-crm", enabled: true },
          { label: "HubSpot", to: "/integrations/hubspot", enabled: true },
          { label: "Salesforce", to: "/integrations/salesforce", enabled: true },
          { label: "Dynamics 365", to: "/integrations/dynamics", enabled: true },
          { label: "NetSuite", to: "/integrations/netsuite", enabled: true },
          { label: "ServiceNow", to: "/integrations/servicenow", enabled: true },
          { label: "SAP", to: "/integrations/sap", enabled: true },
          { label: "Pipedrive", to: "/integrations/pipedrive", enabled: true },
          { label: "Zoho CRM", to: "/integrations/zoho", enabled: true },
          // ── E-commerce ──
          { label: "Shopify", to: "/integrations/shopify", enabled: true },
          { label: "BigCommerce", to: "/integrations/bigcommerce", enabled: true },
          { label: "WooCommerce", to: "/integrations/woocommerce", enabled: true },
          { label: "Magento", to: "/integrations/magento", enabled: true },
          { label: "Square", to: "/integrations/square-appointments", enabled: true },
          { label: "Klaviyo", to: "/integrations/klaviyo", enabled: true },
          { label: "Stripe Payments", to: "/integrations/payments/stripe", enabled: true },
          // ── Hospitality ──
          { label: "Uplisting", to: "/integrations/uplisting", enabled: true },
          { label: "Guesty", to: "/integrations/guesty", enabled: true },
          { label: "Cloudbeds", to: "/integrations/cloudbeds", enabled: true },
          // ── POS / Booking ──
          { label: "Toast POS", to: "/integrations/toast", enabled: true },
          { label: "Clover POS", to: "/integrations/clover", enabled: true },
          { label: "Mindbody", to: "/integrations/mindbody", enabled: true },
          { label: "QuickBooks", to: "/integrations/quickbooks", enabled: true },
          // ── Scheduling ──
          { label: "Outlook Calendar", to: "/integrations/outlook-calendar", enabled: true },
          { label: "Calendly", to: "/integrations/calendly", enabled: true },
          { label: "Microsoft Bookings", to: "/integrations/microsoft-bookings", enabled: true },
          // ── Field Service ──
          { label: "Jobber", to: "/integrations/jobber", enabled: true },
          // ── Government / Vertical ──
          { label: "Accela Civic", to: "/integrations/accela-civic", enabled: true },
          { label: "Formstack", to: "/integrations/formstack", enabled: true },
          // ── HR / Recruiting ──
          { label: "Recruiting Bot", to: "/integrations/recruiting", enabled: true },
          // ── VoIP / CTI ──
          { label: "VAST Retail", to: "/integrations/vast", enabled: true },
          { label: "Nextiva", to: "/integrations/nextiva", enabled: true },
          // ── Email Inbox ──
          { label: "Gmail Inbox", to: "/integrations/gmail-inbox", enabled: true },
          { label: "Outlook Inbox", to: "/integrations/outlook-inbox", enabled: true },
          // ── Productivity ──
          { label: "Microsoft 365", to: "/integrations/microsoft-365", enabled: true },
          { label: "Teams Notifications", to: "/integrations/teams-notifications", enabled: true },
          { label: "Teams Channel", to: "/integrations/teams-channel", enabled: true },
          { label: "Slack", to: "/integrations/slack-notifications", enabled: true },
          { label: "OneDrive", to: "/integrations/microsoft-365/onedrive", enabled: true },
          // ── Automation ──
          { label: "Power Automate", to: "/integrations/power-automate", enabled: true },
          { label: "Make (Integromat)", to: "/integrations/make", enabled: true },
          { label: "Zapier", to: "/integrations/zapier", enabled: true },
          // ── Platform ──
          { label: "MCP Servers", to: "/integrations/mcp", enabled: true },
          { label: "Google Tag Manager", to: "/integrations/google-tag-manager", enabled: true },
          { label: "SecureForms", to: "/integrations/secure-forms", enabled: true },
          { label: "Gorgias Migration", to: "/integrations/gorgias", enabled: true },
          // ── Data ──
          { label: "Transcript Storage", to: "/integrations/transcript-storage", enabled: true },
          { label: "Integration Log", to: "/integrations/log", enabled: true }
        ],
        enabled: true
      },
      // Velaro-internal tools — only visible to site 1032
      {
        label: "Velaro Admin",
        icon: <IconGeneralSettings />,
        enabled: isVelaroAdmin && isVelaroEmail,
        sublinks: [
          {
            label: "Velaro Logger",
            to: "/admin/velaro-logger",
            enabled: true
          },
          {
            label: "Superadmin",
            to: "/admin/database-tool",
            enabled: true
          },
          {
            label: "KB Custom Domains",
            to: "/admin/kb-domains",
            enabled: true
          },
          {
            label: "Site Sync",
            to: "/admin/site-sync",
            enabled: true
          },
          {
            label: "App Marketplace",
            to: "/admin/app-marketplace",
            enabled: true
          },
          {
            label: "System Health",
            to: "/admin/system-health",
            enabled: true
          },
          {
            label: "Live Monitor",
            to: "/admin/live-monitor",
            enabled: true
          },
          {
            label: "Hangfire Monitor",
            to: "/admin/hangfire",
            enabled: true
          },
          {
            label: "Account Health",
            to: "/admin/account-health",
            enabled: true
          },
          {
            label: "AM Assignments",
            to: "/admin/am-assignments",
            enabled: true
          },
          {
            label: "AI Platform",
            to: "/admin/copilot",
            enabled: true
          },
          {
            label: "Activity Log",
            to: "/admin/activity-log",
            enabled: true
          },
          {
            label: "Deploy History",
            to: "/admin/deploy-history",
            enabled: true
          },
          {
            label: "Workflow Logs",
            to: "/admin/workflow-logs",
            enabled: true
          },
          {
            label: "Background Job Migration",
            to: "/admin/background-jobs",
            enabled: true
          },
          {
            label: "Skill Traces",
            to: "/admin/skill-traces",
            enabled: true
          },
          {
            label: "Skill Routing Overrides",
            to: "/admin/skill-routing-overrides",
            enabled: true
          },
          {
            label: "Platform Setup",
            to: "/admin/platform-setup",
            enabled: true
          },
          {
            label: "AI Prompt Policies",
            to: "/admin/ai-prompt-policy",
            enabled: true
          },
          /* DEAD — removed from nav, available in marketing repo. Kept for 30s revival.
          {
            label: "MF Athletic Demo",
            to: "/admin/mf-athletic-demo",
            enabled: true
          }, */
          {
            label: "Support Tools",
            to: "/admin/support-tools",
            enabled: true
          }
        ]
      },
      // Velaro Support — visible to any @velaro.com staff with isVelaroSupport or isVelaroAdmin flag
      // who are NOT full admins (to avoid duplicating items already in the Velaro Admin section above).
      {
        label: "Velaro Support",
        icon: <IconGeneralSettings />,
        enabled: isVelaroStaff && !isVelaroAdmin && isVelaroEmail,
        sublinks: [
          {
            label: "Velaro Logger",
            to: "/admin/velaro-logger",
            enabled: true
          },
          {
            label: "Deployments",
            to: "/admin/deployments",
            enabled: true
          },
          {
            label: "Deploy History",
            to: "/admin/deploy-history",
            enabled: true
          },
          {
            label: "Workflow Logs",
            to: "/admin/workflow-logs",
            enabled: true
          },
          {
            label: "Background Job Migration",
            to: "/admin/background-jobs",
            enabled: true
          },
          {
            label: "Skill Traces",
            to: "/admin/skill-traces",
            enabled: true
          },
          {
            label: "Skill Routing Overrides",
            to: "/admin/skill-routing-overrides",
            enabled: true
          }
        ]
      },
      // Help & Support is intentionally last — always pinned to the bottom of the nav
      {
        label: "Help & Support",
        icon: <IconHelp />,
        enabled: true,
        sublinks: [
          {
            label: "Help Center",
            to: "https://help.velaro.com/",
            enabled: true
          },
          {
            label: "Contact Support",
            to: "/support",
            enabled: true
          }
        ]
      }
    ];
  }, [subscription]);

  return menuItems;
}
